import { Badge, Button, Checkbox, Layout } from "antd";
import styled from "styled-components";

/** 沙盒页面根容器，固定占满当前路由视口，不把滚动传播到浏览器页面。 */
export const SandboxRoot = styled.div`
  height: 100%;
  max-height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  color: #d8e2ef;
  background: #08111f;

  .ant-typography,
  .ant-empty-description {
    color: #94a3b8 !important;
  }

  .ant-tag {
    color: #d8e2ef;
    background: rgba(37, 52, 74, .82);
    border-color: #34445f;
  }

  @media (max-width: 900px) {
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior: contain;
  }
`;

/** 顶部开始游戏工具栏，固定高度并禁止内容撑开页面。 */
export const SandboxToolbar = styled.div`
  flex: 0 0 58px;
  min-height: 58px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 16px;
  overflow: hidden;
  background: #101827;
  border-bottom: 1px solid #26364f;

  @media (max-width: 900px) {
    min-height: auto;
    flex: 0 0 auto;
    align-items: flex-start;
    flex-wrap: wrap;
    padding: 10px;
    overflow: visible;

    > .ant-space:last-child {
      width: 100%;
      row-gap: 8px;
    }

    .ant-select {
      flex: 1 1 230px;
      width: auto !important;
      min-width: 0;
    }
  }

  @media (max-width: 480px) {
    .ant-typography-secondary {
      display: none;
    }
  }
`;

/** 状态统计行，保持单行展示，减少纵向占用。 */
export const StatGrid = styled.div`
  flex: 0 0 44px;
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 8px;
  padding: 6px 12px;
  overflow: hidden;

  @media (max-width: 900px) {
    flex: 0 0 auto;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    padding: 8px;
    overflow: visible;
  }
`;

/** 紧凑状态卡片。 */
export const StatCard = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  background: #121c2c;
  border: 1px solid #26364f;
  border-radius: 6px;
  padding: 4px 10px;
`;

/** 状态卡片标签。 */
export const StatLabel = styled.div`
  flex: 0 1 auto;
  min-width: 0;
  color: #94a3b8;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** 状态卡片数值。 */
export const StatValue = styled.div`
  flex: 0 0 auto;
  color: #f8fafc;
  font-size: 16px;
  font-weight: 700;
  white-space: nowrap;
`;

/** 主内容三栏：战场、材料库、事件日志。 */
export const StatusBar = styled.div`
  flex: 0 0 34px;
  min-height: 34px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 0 12px 6px;
  overflow: hidden;

  @media (max-width: 900px) {
    flex: 0 0 auto;
    min-height: 0;
    align-items: flex-start;
    flex-wrap: wrap;
    padding: 0 8px 8px;
    overflow: visible;
  }
`;

export const StatusGroup = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  overflow: hidden;
  white-space: nowrap;

  @media (max-width: 900px) {
    flex-wrap: wrap;
    overflow: visible;
    white-space: normal;
  }
`;

export const SandboxLayout = styled(Layout)`
  flex: 1 1 auto;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(0, 1.55fr) minmax(0, .7fr) minmax(0, .82fr);
  gap: 10px;
  padding: 0 12px 12px;
  overflow: hidden;
  background: transparent;

  @media (max-width: 900px) {
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 0 8px 12px;
    overflow: visible;
  }
`;

/** 三栏中的单列容器。 */
export const SandboxColumn = styled.div`
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow: hidden;

  @media (max-width: 900px) {
    flex: 0 0 auto;
    gap: 8px;
    overflow: visible;
  }
`;

/** 固定面板。 */
export const SandboxSection = styled.section<{ $fill?: boolean; $fixedHeight?: number }>`
  min-width: 0;
  min-height: 0;
  flex: ${(props) => props.$fill ? "1 1 auto" : props.$fixedHeight ? `0 0 ${props.$fixedHeight}px` : "0 0 auto"};
  height: ${(props) => props.$fixedHeight ? `${props.$fixedHeight}px` : "auto"};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  color: #d8e2ef;
  background: #101827;
  border: 1px solid #26364f;
  border-radius: 8px;
  padding: 8px;

  @media (max-width: 900px) {
    flex: 0 0 auto;
    height: auto;
    min-height: ${(props) => props.$fill ? "340px" : "140px"};
    overflow: hidden;
  }
`;

/** 面板标题行。 */
export const SandboxSectionHeader = styled.div`
  flex: 0 0 24px;
  min-height: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
  overflow: hidden;
  font-weight: 600;
`;

/** 盘面网格，固定 4x5 等比例填充面板剩余空间。 */
export const BoardGrid = styled.div<{ $rows: number; $cols: number }>`
  flex: 1 1 auto;
  min-height: 0;
  display: grid;
  grid-template-columns: repeat(${(props) => props.$cols}, minmax(0, 1fr));
  grid-template-rows: repeat(${(props) => props.$rows}, minmax(0, 1fr));
  gap: 6px;
  overflow: hidden;

  @media (max-width: 900px) {
    flex: 0 0 auto;
    min-height: min(72vw, 430px);
  }
`;

/** 盘面格子。 */
export const BoardCell = styled.button<{ $selected?: boolean; $empty?: boolean; $rarity?: number }>`
  min-width: 0;
  min-height: 0;
  position: relative;
  color: #d8e2ef;
  border: 1px solid ${(props) => props.$selected ? "#f59e0b" : props.$empty ? "#26364f" : props.$rarity === 2 ? "#2f6b4f" : props.$rarity === 3 ? "#2f5c96" : props.$rarity === 4 ? "#6a4aa0" : "#34445f"};
  background: ${(props) => props.$empty ? "#141d2b" : props.$rarity === 2 ? "#143324" : props.$rarity === 3 ? "#142a46" : props.$rarity === 4 ? "#2d2048" : "#1b2432"};
  border-radius: 6px;
  padding: 7px;
  text-align: left;
  cursor: pointer;
  overflow: hidden;
  box-shadow: ${(props) => props.$selected ? "0 0 0 2px rgba(245,158,11,.22)" : "none"};

  &:hover {
    border-color: ${(props) => props.$selected ? "#f59e0b" : "#1677ff"};
  }

  @media (max-width: 480px) {
    padding: 4px;
  }
`;

export const CellContentRow = styled.div`
  height: 100%;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  overflow: hidden;
`;

export const CellTagBadge = styled.span`
  max-width: calc(100% - 14px);
  position: absolute;
  top: 5px;
  right: 5px;
  z-index: 1;
  padding: 1px 5px;
  color: #dbeafe;
  background: rgba(30, 41, 59, .86);
  border: 1px solid rgba(148, 163, 184, .40);
  border-radius: 999px;
  font-size: 10px;
  line-height: 14px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;
`;

export const CellTimerBadge = styled.span`
  position: absolute;
  left: 5px;
  bottom: 5px;
  z-index: 1;
  max-width: calc(100% - 10px);
  padding: 1px 5px;
  color: #e0f2fe;
  background: rgba(14, 116, 144, .42);
  border: 1px solid rgba(103, 232, 249, .42);
  border-radius: 999px;
  font-size: 10px;
  line-height: 14px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;
`;

export const CellTextBlock = styled.div`
  min-width: 0;
  min-height: 0;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 3px;
  overflow: hidden;
`;

/** 格子或实例名称。 */
export const CellName = styled.div<{ $empty?: boolean }>`
  font-weight: 600;
  color: ${(props) => props.$empty ? "#7f8da3" : "#f1f5f9"};
  font-size: 12px;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** 格子或实例数值摘要。 */
export const CellMeta = styled.div`
  color: #cbd5e1;
  font-size: 11px;
  line-height: 1.2;
  font-weight: 600;
  overflow: hidden;
`;

export const CellValueRow = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 3px;
  overflow: hidden;
  white-space: nowrap;
`;

export const CellValueBadge = styled.span<{ $tone: "base" | "grow" | "collect" }>`
  flex: 0 1 auto;
  min-width: 0;
  max-width: 42px;
  padding: 1px 5px;
  color: ${(props) => props.$tone === "grow" ? "#bbf7d0" : props.$tone === "collect" ? "#fde68a" : "#f8fafc"};
  background: ${(props) => props.$tone === "grow" ? "rgba(22, 101, 52, .42)" : props.$tone === "collect" ? "rgba(146, 64, 14, .42)" : "rgba(248, 250, 252, .12)"};
  border: 1px solid ${(props) => props.$tone === "grow" ? "rgba(74, 222, 128, .42)" : props.$tone === "collect" ? "rgba(250, 204, 21, .46)" : "rgba(226, 232, 240, .38)"};
  border-radius: 999px;
  font-size: 10px;
  line-height: 14px;
  font-weight: 700;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** 固定槽位网格，用于 1x6 辅料栏和配方栏。 */
export const FixedSlotGrid = styled.div<{ $cols: number }>`
  flex: 1 1 auto;
  min-height: 0;
  display: grid;
  grid-template-columns: repeat(${(props) => props.$cols}, minmax(0, 1fr));
  grid-template-rows: 1fr;
  gap: 6px;
  overflow: hidden;

  @media (max-width: 600px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    grid-template-rows: repeat(2, minmax(54px, 1fr));
    min-height: 116px;
  }
`;

/** 自动填充实例网格，用于材料库。超出容器的内容会被裁剪，避免出现滚动条。 */
export const InstanceGrid = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
  grid-auto-rows: 62px;
  gap: 6px;
  overflow: hidden;
`;

/** 实例卡片。 */
export const ScrollableInstanceGrid = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
  grid-auto-rows: 62px;
  align-content: start;
  gap: 6px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 2px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #334155;
    border-radius: 999px;
  }

  @media (max-width: 900px) {
    max-height: 330px;
    grid-template-columns: repeat(auto-fill, minmax(104px, 1fr));
  }
`;

/** 深色沙盒里使用的低干扰数量标记，避免默认红色产生警告感。 */
export const QuietBadge = styled(Badge)`
  .ant-badge-count {
    min-width: 20px;
    height: 18px;
    line-height: 18px;
    color: #d8e2ef;
    background: #334155;
    border: 1px solid #475569;
    box-shadow: none;
    font-weight: 600;
  }
`;

export const InstanceCard = styled.button<{ $active?: boolean; $empty?: boolean; $emptyInteractive?: boolean; $rarity?: number }>`
  min-width: 0;
  min-height: 0;
  position: relative;
  color: #d8e2ef;
  border: 1px solid ${(props) => props.$active ? "#f59e0b" : props.$emptyInteractive ? "#d97706" : props.$empty ? "#26364f" : props.$rarity === 2 ? "#2f6b4f" : props.$rarity === 3 ? "#2f5c96" : props.$rarity === 4 ? "#6a4aa0" : "#34445f"};
  border-style: ${(props) => props.$emptyInteractive ? "dashed" : "solid"};
  background: ${(props) => props.$empty ? "#141d2b" : props.$rarity === 2 ? "#143324" : props.$rarity === 3 ? "#142a46" : props.$rarity === 4 ? "#2d2048" : "#1b2432"};
  border-radius: 6px;
  padding: 6px;
  text-align: left;
  overflow: hidden;
  cursor: ${(props) => props.$empty && !props.$emptyInteractive ? "default" : "pointer"};
  box-shadow: ${(props) => props.$active ? "0 0 0 2px rgba(245,158,11,.22)" : props.$emptyInteractive ? "inset 0 0 0 1px rgba(245,158,11,.20)" : "none"};

  &::after {
    content: ${(props) => props.$emptyInteractive ? '"可选"' : "none"};
    position: absolute;
    right: 6px;
    bottom: 4px;
    color: #fbbf24;
    font-size: 11px;
    line-height: 1;
    pointer-events: none;
  }

  &:hover {
    border-color: ${(props) => props.$active ? "#f59e0b" : props.$emptyInteractive ? "#fbbf24" : props.$empty ? "#26364f" : "#4f9dff"};
  }
`;

export const InstanceRemoveControl = styled.span<{ $disabled?: boolean }>`
  position: absolute;
  right: 5px;
  bottom: 5px;
  z-index: 2;
  width: 18px;
  height: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.$disabled ? "#64748b" : "#94a3b8"};
  background: rgba(15, 23, 42, .76);
  border: 1px solid ${(props) => props.$disabled ? "#334155" : "#475569"};
  border-radius: 4px;
  font-size: 11px;
  line-height: 1;
  cursor: ${(props) => props.$disabled ? "not-allowed" : "pointer"};
  opacity: ${(props) => props.$disabled ? .55 : .86};

  &:hover {
    color: ${(props) => props.$disabled ? "#64748b" : "#fca5a5"};
    border-color: ${(props) => props.$disabled ? "#334155" : "#ef4444"};
    background: ${(props) => props.$disabled ? "rgba(15, 23, 42, .76)" : "rgba(127, 29, 29, .36)"};
  }
`;

/** 材料库下方的独立调和按钮，高度与配方栏/辅料栏一致。 */
export const HarmonyButton = styled(Button)`
  flex: 0 0 96px;
  width: 100%;
  height: 96px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;

  .harmony-title {
    font-size: 22px;
    font-weight: 800;
    line-height: 1;
  }

  .harmony-subtitle {
    font-size: 12px;
    font-weight: 500;
    line-height: 1.2;
    opacity: .82;
  }

  @media (max-width: 900px) {
    flex-basis: 72px;
    height: 72px;
  }
`;

/** 日志列表容器。内容超出时裁剪，避免页面出现滚动条。 */
export const ScrollList = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #334155;
    border-radius: 999px;
  }

  @media (max-width: 900px) {
    max-height: 360px;
  }
`;

/** 事件日志筛选行，固定在日志列表上方，不参与日志滚动。 */
export const LogFilterRow = styled.div`
  flex: 0 0 auto;
  display: block;
  margin-bottom: 6px;
`;

/** 事件类型多选，放在标题栏中，使用紧凑间距避免挤占日志区域。 */
export const LogCategoryChecks = styled(Checkbox.Group)`
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;

  .ant-checkbox-wrapper {
    margin-inline-start: 0;
    color: #cbd5e1;
    font-size: 12px;
  }

  @media (max-width: 600px) {
    flex-wrap: wrap;
    white-space: normal;
  }
`;

/** 事件日志条目。 */
export const LogItem = styled.div`
  border-bottom: 1px solid #26364f;
  padding: 6px 0;
  font-size: 12px;
  overflow: hidden;
`;

/** 事件日志标题。 */
export const LogTitle = styled.div`
  color: #e2e8f0;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** 事件日志元信息。 */
export const LogMeta = styled.div`
  color: #8ea0b8;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
